import { useState, useEffect, useCallback } from "react";
import { useAppSelector, useAppDispatch, RootState } from "src/store/store";
import { AuditResult, getFilteredAuditData } from "src/store/auditSlice";
import { getAuditJobHistory } from "src/store/auditDataUploadSlice";
import { Incident, AuditDataItem, DataFilter, FilterCondition } from "./types";
import { processApiData, isFieldRelevant } from "./utils";
import { TimePeriodType } from "src/services/timePeriodService";
import { ExportByIdsRequest } from "src/services/auditWorkflowServeice";
import { exportFilteredAuditDataByIds, exportFilteredAuditData } from "src/store/auditSlice";
import { ADMIN_LDAP_GROUP, ENGINEER_LDAP_GROUP, CPT_AUDITOR_ADMIN_LDAP_GROUP } from "src/constants";

export const useAuditDashboard = () => {
  const dispatch = useAppDispatch();

  // Get filtered audit data from Redux store
  const {
    filteredAuditData,
    getFilteredAuditDataLoading,
    exportFilteredAuditDataByIdsLoading,
    exportFilteredAuditDataLoading,
  } = useAppSelector((state: RootState) => state.auditSlice);

  // Get user LDAP groups for default tab logic
  const { ldap } = useAppSelector((state) => state.user);

  // Check if user can see metrics tabs (admin and engineer only, not CPT admin)
  const canSeeMetricsTabs = ldap.includes(ADMIN_LDAP_GROUP) || ldap.includes(ENGINEER_LDAP_GROUP);

  // State management - default tab based on user permissions
  const [currentTab, setCurrentTab] = useState(
    canSeeMetricsTabs ? "tab-dqo-metrics" : "tab-all-audits",
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [showAllFields, setShowAllFields] = useState(false);
  const [includeAllFields, setIncludeAllFields] = useState(false);
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");
  const [dateRange, setDateRange] = useState<any[]>([]);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [selectedAuditResults, setSelectedAuditResults] = useState<string[]>([]);
  // Default select "EHS Smart Assistant" for admin and engineer users
  const [selectedAISources, setSelectedAISources] = useState<string[]>(
    canSeeMetricsTabs ? ["EHS Smart Assistant"] : [],
  );
  const [selectedSiteTypes, setSelectedSiteTypes] = useState<string[]>([]);
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [selectedBusinessUnits, setSelectedBusinessUnits] = useState<string[]>([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filteredData, setFilteredData] = useState<AuditDataItem[]>([]);
  const [allIncidents, setAllIncidents] = useState<Incident[]>([]);
  const [filteredIncidents, setFilteredIncidents] = useState<Incident[]>([]);
  const [displayedIncidents, setDisplayedIncidents] = useState<Incident[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  // Initialize selectedRows from localStorage
  const [selectedRows, setSelectedRowsState] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem("auditDashboard_selectedRows");
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error("Error loading selected rows from localStorage:", error);
      return [];
    }
  });

  // Custom setter that also saves to localStorage
  const setSelectedRows = (rows: string[]) => {
    try {
      setSelectedRowsState(rows);
      if (rows.length === 0) {
        localStorage.removeItem("auditDashboard_selectedRows");
      } else {
        localStorage.setItem("auditDashboard_selectedRows", JSON.stringify(rows));
      }
    } catch (error) {
      console.error("Error saving selected rows to localStorage:", error);
      setSelectedRowsState(rows);
    }
  };
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedAuditor, setSelectedAuditor] = useState("");
  const [selectedPeriodType, setSelectedPeriodType] = useState<TimePeriodType>(
    TimePeriodType.MONTHLY,
  );
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState<number>(0);

  // New state for audit workflow filtering - default to workflow for CPT Admin users, date for others
  const [filterMode, setFilterMode] = useState<"date" | "workflow">(
    ldap.includes(CPT_AUDITOR_ADMIN_LDAP_GROUP) ? "workflow" : "date",
  );
  const [selectedWorkflowIds, setSelectedWorkflowIds] = useState<string[]>([]);

  // Get audit workflows from Redux store
  const { auditJobs } = useAppSelector((state: RootState) => state.auditDataUploadSlice);

  // Generate filters in the correct format for the backend
  const generateFilters = (): DataFilter[] => {
    const filters: DataFilter[] = [];

    if (filterMode === "date") {
      // Add date range filter if selected
      if (dateRange.length === 2 && dateRange[0] && dateRange[1]) {
        try {
          const startDate = new Date(dateRange[0]);
          const endDate = new Date(dateRange[1]);
          const startIso = startDate.toISOString();
          const endIso = endDate.toISOString();

          filters.push({
            attributeName: "date_reported",
            attributeValues: [startIso, endIso],
            condition: FilterCondition.BETWEEN,
          });
        } catch (error) {
          console.error("Error formatting date range:", error);
        }
      }
    } else if (filterMode === "workflow") {
      // Add workflow ID filter if selected
      if (selectedWorkflowIds.length > 0) {
        if (selectedWorkflowIds.length === 1) {
          // Use EQUAL for single ID (more efficient query)
          filters.push({
            attributeName: "e.audit_workflow_id",
            attributeValues: [selectedWorkflowIds[0]],
            condition: FilterCondition.EQUALS,
          });
        } else {
          // Use IN for multiple IDs
          filters.push({
            attributeName: "e.audit_workflow_id",
            attributeValues: selectedWorkflowIds,
            condition: FilterCondition.IN,
          });
        }
      }
    }

    return filters;
  };

  // Fetch audit job history when the component mounts
  useEffect(() => {
    dispatch(getAuditJobHistory());
  }, [dispatch]);

  // Only fetch data when the search button is clicked
  // The useEffect dependency on currentTab ensures we still update when switching tabs
  useEffect(() => {
    // Just reset data when switching tabs
    if (currentTab !== "tab-all-audits") {
      setFilteredData([]);
      setFilteredIncidents([]);
      setAllIncidents([]);
    }
  }, [currentTab]);

  // Update date range when period selection changes or when View Audits tab becomes active
  useEffect(() => {
    if (currentTab === "tab-all-audits") {
      const now = new Date();
      let startDate: Date;
      let endDate: Date;

      if (selectedPeriodType === TimePeriodType.WEEKLY) {
        const currentDate = new Date(now.getTime() - selectedPeriodIndex * 7 * 24 * 60 * 60 * 1000);
        const dayOfWeek = currentDate.getDay();
        startDate = new Date(currentDate.getTime() - dayOfWeek * 24 * 60 * 60 * 1000);
        endDate = new Date(startDate.getTime() + 6 * 24 * 60 * 60 * 1000);
      } else {
        // MONTHLY
        startDate = new Date(now.getFullYear(), now.getMonth() - selectedPeriodIndex, 1);
        if (selectedPeriodIndex === 0) {
          endDate = now;
        } else {
          endDate = new Date(now.getFullYear(), now.getMonth() - selectedPeriodIndex + 1, 0);
        }
      }

      const newDateRange = [
        startDate.toISOString().split("T")[0],
        endDate.toISOString().split("T")[0],
      ];
      setDateRange(newDateRange);
    }
  }, [currentTab, selectedPeriodType, selectedPeriodIndex]);

  // Handle sorting using Meridian's built-in onSort callback
  const onSort = useCallback(
    ({ sortColumn, sortDirection }: { sortColumn: string; sortDirection: string }) => {
      setSortColumn(sortColumn);
      setSortDirection(sortDirection);
    },
    [],
  );

  // Process API data when it changes and apply client-side filtering
  useEffect(() => {
    if (filteredAuditData && filteredAuditData.length > 0) {
      const { incidents, auditItems } = processApiData(filteredAuditData, includeAllFields);

      // Store the complete set of incidents for filter dropdowns
      setAllIncidents(incidents);

      let filteredIncidentsResult = [...incidents];
      let filteredAuditItemsResult = [...auditItems];

      // Apply field filters if selected - only show incidents with defects in selected fields
      if (selectedFields.length > 0) {
        filteredIncidentsResult = filteredIncidentsResult.filter((incident) => {
          return incident.fields.some(
            (field) =>
              selectedFields.includes(field.fieldName) &&
              (field.auditResult === AuditResult.EHS_SMART_ASSISTANT_CORRECT ||
                field.auditResult === AuditResult.LLM_CORRECT ||
                field.auditResult === AuditResult.HUMAN_CORRECT ||
                field.auditResult === AuditResult.NEITHER),
          );
        });

        filteredAuditItemsResult = filteredAuditItemsResult.filter(
          (item) =>
            selectedFields.includes(item.fieldName) &&
            (item.auditResult === AuditResult.EHS_SMART_ASSISTANT_CORRECT ||
              item.auditResult === AuditResult.LLM_CORRECT ||
              item.auditResult === AuditResult.HUMAN_CORRECT ||
              item.auditResult === AuditResult.NEITHER),
        );
      }

      // Apply defect type filters if selected
      if (selectedAuditResults.length > 0) {
        filteredIncidentsResult = filteredIncidentsResult.filter((incident) => {
          return incident.fields.some((field) => {
            // Consider either core fields only OR all fields based on toggle state
            if (!isFieldRelevant(field.fieldName, includeAllFields)) {
              return false;
            }

            let matchesFilter = false;

            // Handle defect type filters
            if (selectedAuditResults.includes("AI Defect")) {
              // AI Defect: Show incidents with AI defects (Human is correct OR Neither is correct)
              if (
                field.auditResult === AuditResult.HUMAN_CORRECT ||
                field.auditResult === AuditResult.NEITHER
              ) {
                matchesFilter = true;
              }
            }

            if (selectedAuditResults.includes("Human Defect")) {
              // Human Defect: Show incidents with Human defects (EHS/AI is correct OR Neither is correct)
              if (
                field.auditResult === AuditResult.EHS_SMART_ASSISTANT_CORRECT ||
                field.auditResult === AuditResult.NEITHER
              ) {
                matchesFilter = true;
              }
            }

            if (selectedAuditResults.includes("Both Defect")) {
              // Both Defect: Show incidents where neither is correct
              if (field.auditResult === AuditResult.NEITHER) {
                matchesFilter = true;
              }
            }

            return matchesFilter;
          });
        });

        filteredAuditItemsResult = filteredAuditItemsResult.filter((item) => {
          // Consider either core fields only OR all fields based on toggle state
          if (!isFieldRelevant(item.fieldName, includeAllFields)) {
            return false;
          }

          let matchesFilter = false;

          // Handle defect type filters
          if (selectedAuditResults.includes("AI Defect")) {
            // AI Defect: Show items with AI defects (Human is correct OR Neither is correct)
            if (
              item.auditResult === AuditResult.HUMAN_CORRECT ||
              item.auditResult === AuditResult.NEITHER
            ) {
              matchesFilter = true;
            }
          }

          if (selectedAuditResults.includes("Human Defect")) {
            // Human Defect: Show items with Human defects (EHS/AI is correct OR Neither is correct)
            if (
              item.auditResult === AuditResult.EHS_SMART_ASSISTANT_CORRECT ||
              item.auditResult === AuditResult.NEITHER
            ) {
              matchesFilter = true;
            }
          }

          if (selectedAuditResults.includes("Both Defect")) {
            // Both Defect: Show items where neither is correct
            if (item.auditResult === AuditResult.NEITHER) {
              matchesFilter = true;
            }
          }

          return matchesFilter;
        });
      }

      // Apply AI Source filters if selected
      if (selectedAISources.length > 0) {
        filteredIncidentsResult = filteredIncidentsResult.filter((incident) => {
          return incident.aiSource && selectedAISources.includes(incident.aiSource);
        });

        filteredAuditItemsResult = filteredAuditItemsResult.filter((item) => {
          // Find the corresponding incident for this audit item
          const correspondingIncident = incidents.find((inc) => inc.caseId === item.caseId);
          return (
            correspondingIncident &&
            correspondingIncident.aiSource &&
            selectedAISources.includes(correspondingIncident.aiSource)
          );
        });
      }

      // Apply site type filters if selected
      if (selectedSiteTypes.length > 0) {
        filteredIncidentsResult = filteredIncidentsResult.filter((incident) => {
          return incident.siteType && selectedSiteTypes.includes(incident.siteType);
        });

        filteredAuditItemsResult = filteredAuditItemsResult.filter((item) => {
          // Find the corresponding incident for this audit item
          const correspondingIncident = incidents.find((inc) => inc.caseId === item.caseId);
          return (
            correspondingIncident &&
            correspondingIncident.siteType &&
            selectedSiteTypes.includes(correspondingIncident.siteType)
          );
        });
      }

      // Apply site filters if selected
      if (selectedSites.length > 0) {
        filteredIncidentsResult = filteredIncidentsResult.filter((incident) => {
          return incident.site && selectedSites.includes(incident.site);
        });

        filteredAuditItemsResult = filteredAuditItemsResult.filter((item) => {
          // Find the corresponding incident for this audit item
          const correspondingIncident = incidents.find((inc) => inc.caseId === item.caseId);
          return (
            correspondingIncident &&
            correspondingIncident.site &&
            selectedSites.includes(correspondingIncident.site)
          );
        });
      }

      // Apply business unit filters if selected
      if (selectedBusinessUnits.length > 0) {
        filteredIncidentsResult = filteredIncidentsResult.filter((incident) => {
          return incident.obr_bu && selectedBusinessUnits.includes(incident.obr_bu);
        });

        filteredAuditItemsResult = filteredAuditItemsResult.filter((item) => {
          // Find the corresponding incident for this audit item
          const correspondingIncident = incidents.find((inc) => inc.caseId === item.caseId);
          return (
            correspondingIncident &&
            correspondingIncident.obr_bu &&
            selectedBusinessUnits.includes(correspondingIncident.obr_bu)
          );
        });
      }

      // Apply organization filters if selected
      if (selectedOrganizations.length > 0) {
        filteredIncidentsResult = filteredIncidentsResult.filter((incident) => {
          return incident.obr_org && selectedOrganizations.includes(incident.obr_org);
        });

        filteredAuditItemsResult = filteredAuditItemsResult.filter((item) => {
          // Find the corresponding incident for this audit item
          const correspondingIncident = incidents.find((inc) => inc.caseId === item.caseId);
          return (
            correspondingIncident &&
            correspondingIncident.obr_org &&
            selectedOrganizations.includes(correspondingIncident.obr_org)
          );
        });
      }

      // Apply scope filters if selected
      if (selectedScopes.length > 0) {
        filteredIncidentsResult = filteredIncidentsResult.filter((incident) => {
          return incident.obr_scope && selectedScopes.includes(incident.obr_scope);
        });

        filteredAuditItemsResult = filteredAuditItemsResult.filter((item) => {
          // Find the corresponding incident for this audit item
          const correspondingIncident = incidents.find((inc) => inc.caseId === item.caseId);
          return (
            correspondingIncident &&
            correspondingIncident.obr_scope &&
            selectedScopes.includes(correspondingIncident.obr_scope)
          );
        });
      }

      setFilteredData(filteredAuditItemsResult);
      setFilteredIncidents(filteredIncidentsResult);
      setCurrentPage(1);
    } else {
      setFilteredData([]);
      setFilteredIncidents([]);
    }
  }, [
    filteredAuditData,
    selectedFields,
    selectedAuditResults,
    selectedAISources,
    selectedSiteTypes,
    selectedSites,
    selectedBusinessUnits,
    selectedOrganizations,
    selectedScopes,
    includeAllFields,
  ]);

  // Apply sorting and pagination
  useEffect(() => {
    let sortedIncidents = [...filteredIncidents];

    if (sortColumn) {
      sortedIncidents.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortColumn) {
          case "caseId":
            aValue = a.caseId;
            bValue = b.caseId;
            break;
          case "defects":
          case "dqo":
            aValue = a.dqoCount;
            bValue = b.dqoCount;
            break;
          case "aiDefect":
            aValue = a.aiDefectCount;
            bValue = b.aiDefectCount;
            break;
          case "reviewer":
            aValue = a.reviewer;
            bValue = b.reviewer;
            break;
          case "siteType":
            aValue = a.siteType;
            bValue = b.siteType;
            break;
          case "site":
            aValue = a.site;
            bValue = b.site;
            break;
          default:
            return 0;
        }

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sortDirection === "ascending"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else {
          return sortDirection === "ascending" ? aValue - bValue : bValue - aValue;
        }
      });
    }

    setDisplayedIncidents(sortedIncidents);
  }, [filteredIncidents, sortColumn, sortDirection]);

  // Calculate pagination
  const numberOfPages = Math.ceil(displayedIncidents.length / itemsPerPage);
  const firstVisibleIndex = (currentPage - 1) * itemsPerPage;
  const lastVisibleIndex = Math.min(displayedIncidents.length, firstVisibleIndex + itemsPerPage);
  const visibleIncidents = displayedIncidents.slice(firstVisibleIndex, lastVisibleIndex);

  // Toggle row selection
  const toggleRowSelection = (incidentId: string) => {
    if (selectedRows.includes(incidentId)) {
      setSelectedRows(selectedRows.filter((id) => id !== incidentId));
    } else {
      setSelectedRows([...selectedRows, incidentId]);
    }
  };

  // Handle export
  const handleExport = async () => {
    try {
      if (selectedRows.length > 0) {
        // Export selected audit
        const exportRequest: ExportByIdsRequest = {
          incident_ids: selectedRows,
        };
        await dispatch(exportFilteredAuditDataByIds(exportRequest));
      } else {
        // Export audit by all audit data filter
        const filters = generateFilters();
        await dispatch(exportFilteredAuditData(filters));
      }
    } catch (error) {
      console.error("Error exporting data:", error);
    }
  };

  // Handle re-audit assignment
  const handleAssign = () => {
    if (!selectedAuditor) {
      console.log("Please select an auditor");
      return;
    }

    console.log(`${selectedRows.length} cases to be assigned to ${selectedAuditor} for re-audit`);
    setIsAssignModalOpen(false);
    setSelectedRows([]); // This will also clear localStorage
    setSelectedAuditor("");
  };

  // Reset filters
  const resetFilters = () => {
    setDateRange([]);
    setSelectedFields([]);
    setSelectedAuditResults([]);
    setSelectedAISources([]);
    setSelectedSiteTypes([]);
    setSelectedSites([]);
    setSelectedBusinessUnits([]);
    setSelectedOrganizations([]);
    setSelectedScopes([]);
  };

  // Manual search function that triggers data fetching
  const performSearch = () => {
    // Reset the filtered data when performing a new search
    setFilteredData([]);
    setFilteredIncidents([]);
    setAllIncidents([]);

    const filters = generateFilters();
    if (
      (filterMode === "date" && dateRange.length === 2 && dateRange[0] && dateRange[1]) ||
      (filterMode === "workflow" && selectedWorkflowIds.length > 0)
    ) {
      dispatch(
        getFilteredAuditData({
          filters,
          limit: 10000,
        }),
      );
    }
  };

  // Handle filter mode change - clear data and filters specific to each mode
  const handleFilterModeChange = (newMode: "date" | "workflow") => {
    // Clear displayed data
    setFilteredData([]);
    setFilteredIncidents([]);
    setAllIncidents([]);

    // Reset mode-specific filters
    if (newMode === "date") {
      setSelectedWorkflowIds([]);
    } else {
      setDateRange([]);
    }

    setFilterMode(newMode);
  };

  // Check if we have any data to enable/disable filters
  const hasData = allIncidents.length > 0;

  return {
    // State
    currentTab,
    setCurrentTab,
    searchTerm,
    setSearchTerm,
    showAllFields,
    setShowAllFields,
    includeAllFields,
    setIncludeAllFields,
    sortColumn,
    sortDirection,
    dateRange,
    setDateRange,
    selectedFields,
    setSelectedFields,
    selectedAuditResults,
    setSelectedAuditResults,
    selectedAISources,
    setSelectedAISources,
    selectedSiteTypes,
    setSelectedSiteTypes,
    selectedSites,
    setSelectedSites,
    selectedBusinessUnits,
    setSelectedBusinessUnits,
    selectedOrganizations,
    setSelectedOrganizations,
    selectedScopes,
    setSelectedScopes,
    isFilterModalOpen,
    setIsFilterModalOpen,
    allIncidents,
    filteredIncidents,
    displayedIncidents,
    visibleIncidents,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    numberOfPages,
    selectedRows,
    setSelectedRows,
    isAssignModalOpen,
    setIsAssignModalOpen,
    selectedAuditor,
    setSelectedAuditor,
    selectedPeriodType,
    setSelectedPeriodType,
    selectedPeriodIndex,
    setSelectedPeriodIndex,
    filterMode,
    hasData,
    selectedWorkflowIds,
    setSelectedWorkflowIds,
    auditJobs,
    exportFilteredAuditDataByIdsLoading,
    exportFilteredAuditDataLoading,

    // Functions
    onSort,
    toggleRowSelection,
    handleExport,
    handleAssign,
    resetFilters,
    performSearch,
    handleFilterModeChange,
    getFilteredAuditDataLoading,
  };
};
